/**
 * Facebook Login, only for connecting Messenger pages.
 *
 * The SDK is fetched the first time someone presses Connect, not on page load:
 * it is a third-party script on every agent's browser, and most workspaces
 * never touch Messenger at all.
 */

interface AuthResponse {
  accessToken: string;
  userID: string;
}

interface FacebookSdk {
  init(options: { appId: string; version: string; cookie?: boolean; xfbml?: boolean }): void;
  login(
    callback: (response: { authResponse?: AuthResponse | null; status?: string }) => void,
    options?: { scope: string; return_scopes?: boolean },
  ): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

/**
 * What the server actually needs, and nothing beyond it.
 *
 *   pages_show_list        list the pages this person administers
 *   pages_messaging        read and send Messenger messages as the page
 *   pages_manage_metadata  subscribe our app to the page's message webhooks
 *
 * Asking for more is asking Meta's reviewers a harder question.
 */
const SCOPES = 'pages_show_list,pages_messaging,pages_manage_metadata';

const SDK_ID = 'facebook-jssdk';

let loading: Promise<FacebookSdk> | null = null;

function loadSdk(appId: string, version: string): Promise<FacebookSdk> {
  if (window.FB) return Promise.resolve(window.FB);
  if (loading) return loading;

  loading = new Promise<FacebookSdk>((resolve, reject) => {
    window.fbAsyncInit = () => {
      if (!window.FB) {
        reject(new Error('Facebook SDK loaded without initialising'));
        return;
      }

      window.FB.init({ appId, version, cookie: false, xfbml: false });
      resolve(window.FB);
    };

    if (document.getElementById(SDK_ID)) return;

    const script = document.createElement('script');
    script.id = SDK_ID;
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.onerror = () => {
      loading = null;
      reject(new Error('Could not reach Facebook. An ad blocker will do this.'));
    };

    document.head.append(script);
  });

  return loading;
}

/**
 * Opens the Facebook consent dialog and returns what the server needs.
 *
 * Resolves to null when the person closes the dialog or declines, which is a
 * normal outcome rather than a failure — the caller should say nothing.
 */
export async function connectFacebook(appId: string, version: string): Promise<AuthResponse | null> {
  const sdk = await loadSdk(appId, version);

  return new Promise<AuthResponse | null>((resolve) => {
    sdk.login(
      (response) => {
        resolve(response.authResponse ?? null);
      },
      { scope: SCOPES },
    );
  });
}
